import { LLMConfig, LLMProvider, LLMProviderType } from '../types';
import { createOllamaProvider } from './providers/ollamaProvider';
import { createOpenAIProvider } from './providers/openaiProvider';
import { createAnthropicProvider } from './providers/anthropicProvider';
import { createAzureOpenAIProvider } from './providers/azureOpenaiProvider';

const getProviderType = (): LLMProviderType => {
  const provider = process.env.REACT_APP_LLM_PROVIDER as LLMProviderType | undefined;
  return provider || 'ollama';
};

const getConfig = (): LLMConfig => {
  const provider = getProviderType();
  console.log('PROVIDER', provider);
  switch (provider) {
    case 'openai':
      return {
        provider: 'openai',
        baseUrl: process.env.REACT_APP_OPENAI_BASE_URL || 'https://api.openai.com/v1',
        // No client-side key: AI requests route through the APIM proxy (azure-openai). Unused otherwise.
        apiKey: undefined,
        model: process.env.REACT_APP_LLM_MODEL || 'gpt-4o-mini',
        temperature: parseFloat(process.env.REACT_APP_LLM_TEMPERATURE || '0.7'),
        maxTokens: parseInt(process.env.REACT_APP_LLM_MAX_TOKENS || '2048', 10),
      };

    case 'anthropic':
      return {
        provider: 'anthropic',
        baseUrl: process.env.REACT_APP_ANTHROPIC_BASE_URL || 'https://api.anthropic.com/v1',
        apiKey: undefined,
        model: process.env.REACT_APP_LLM_MODEL || 'claude-3-haiku-20240307',
        temperature: parseFloat(process.env.REACT_APP_LLM_TEMPERATURE || '0.7'),
        maxTokens: parseInt(process.env.REACT_APP_LLM_MAX_TOKENS || '2048', 10),
      };

    case 'azure-openai':
      // Route through the APIM AI proxy. The Azure OpenAI key is injected server-side by APIM
      // and is never present in the client bundle. The AzureOpenAI SDK builds
      // `${endpoint}/openai/deployments/{deployment}/chat/completions`, which matches the proxy.
      const endpoint = (process.env.REACT_APP_AI_PROXY_BASE_URL || '').replace(/\/+$/, '');
      return {
        provider: 'azure-openai',
        baseUrl: endpoint,
        // Placeholder only — the AzureOpenAI SDK requires a non-empty key, but APIM overrides the
        // api-key header with the real key, so this value is never used to authenticate.
        apiKey: 'apim-proxy',
        model: process.env.REACT_APP_LLM_MODEL || 'gpt-4.1',
        deployment: process.env.REACT_APP_AZURE_OPENAI_DEPLOYMENT || 'gpt-4.1',
        apiVersion: process.env.REACT_APP_AZURE_OPENAI_API_VERSION || '2024-04-01-preview',
        temperature: parseFloat(process.env.REACT_APP_LLM_TEMPERATURE || '0.7'),
        maxTokens: parseInt(process.env.REACT_APP_LLM_MAX_TOKENS || '2048', 10),
      };

    case 'ollama':
    default:
      return {
        provider: 'ollama',
        baseUrl: process.env.REACT_APP_OLLAMA_URL || 'http://localhost:11434',
        model: process.env.REACT_APP_LLM_MODEL || 'llama3:8b',
        temperature: parseFloat(process.env.REACT_APP_LLM_TEMPERATURE || '0.7'),
        maxTokens: parseInt(process.env.REACT_APP_LLM_MAX_TOKENS || '2048', 10),
      };
  }
};

export const createLLMProvider = (): LLMProvider => {
  const config = getConfig();

  switch (config.provider) {
    case 'openai':
      return createOpenAIProvider(config);
    case 'anthropic':
      return createAnthropicProvider(config);
    case 'azure-openai':
      return createAzureOpenAIProvider(config);
    case 'ollama':
    default:
      return createOllamaProvider(config);
  }
};

export const createLLMProviderWithConfig = (overrides?: Partial<LLMConfig>): LLMProvider => {
  const baseConfig = getConfig();
  const config: LLMConfig = overrides ? { ...baseConfig, ...overrides } : baseConfig;

  switch (config.provider) {
    case 'openai':
      return createOpenAIProvider(config);
    case 'anthropic':
      return createAnthropicProvider(config);
    case 'azure-openai':
      return createAzureOpenAIProvider(config);
    case 'ollama':
    default:
      return createOllamaProvider(config);
  }
};
